<script setup>

import { inject, ref, computed, watch } from 'vue';
import { 
    apiAjouterTypeObjet, 
    apiModifierInfosTypeObjet } from '@/services/ServiceModele.js';
import { normerNom, TypeNommage } from '@/scripts/global.js';
import { 
    getErreurNomNorme,
    getErreurTexteRequis, 
    getErreurValeurListeRequise } from '@/scripts/controlesSaisie.js';
import Dialog from 'primevue/dialog';
import InputSwitch from 'primevue/inputswitch';
import Formulaire from '@/components/generiques/Formulaire.vue';
import SaisieTexte from '@/components/generiques/SaisieTexte.vue';
import SaisieTypeObjet from '@/components/generiques/SaisieTypeObjet.vue';
import SaisieTypeOccurrence from '@/components/generiques/SaisieTypeOccurrence.vue';
import SaisieTypeDonnee from '@/components/generiques/SaisieTypeDonnee.vue';

const typesObjetMultiples = inject('typesObjetMultiples');
const getTypeObjet = inject('getTypeObjet');
const getChampsCles = inject('getChampsCles');
const getTypeDonnee = inject('getTypeDonnee');
const appelerApiModifModele = inject('appelerApiModifModele');
const typesDonneePrimitifs = inject('typesDonneePrimitifs');

const { typeObjetEdite } = defineProps({
    typeObjetEdite: Object
});

const emit = defineEmits(['fermeture']);

const typeObjet = ref(getTypeObjetInitial());

const estNouveauTypeObjet = computed(() => {
    return !(typeObjetEdite.id > 0);
});

watch(() => typeObjet.value.typeObjetPere, (typeObjetPere) => {
    if (typeObjetPere) {
        typeObjet.value.typeOccurrence = typeObjetPere.typeOccurrence;
    }
})

watch(() => typeObjet.value.libelle, (nouveauLibelle, ancienLibelle) => {
    if (!typeObjet.value.nom || typeObjet.value.nom === normerNom(ancienLibelle, TypeNommage.CLASSE)) {
        typeObjet.value.nom = normerNom(nouveauLibelle, TypeNommage.CLASSE);
    }
});

watch(() => typeObjet.value.typeOccurrence, (typeOccurrence) => {
    if (typeOccurrence === 'PLUSIEURS') {
        typeObjet.value.champsCles = [{
            nom: 'id', 
            typeDonnee: typesDonneePrimitifs.find(type => type.libelle === 'Texte')
        }];
        typeObjet.value.cleAuto = false;
    } else {
        typeObjet.value.champsCles = [];
    }
});

watch(() => typeObjet.value.cleAuto, (cleAuto) => {
    if (cleAuto) {
        let champCleAuto;
        if (!estNouveauTypeObjet.value) {
            if (typeObjetEdite.cleAuto) {
                champCleAuto = getChampsCles(typeObjetEdite)[0];
            } else {
                let numSuffixe = 1;
                let nomChampCleAuto = 'id';
                if (typeObjetEdite.champs.some(chp => chp.nom === nomChampCleAuto)) {
                    do {
                        numSuffixe++;
                        nomChampCleAuto = `id_${numSuffixe}`;
                    } while (typeObjetEdite.champs.some(chp => chp.nom === nomChampCleAuto));
                }
                champCleAuto = {
                    id: -1,
                    nom: nomChampCleAuto, 
                    typeDonnee: typesDonneePrimitifs.find(type => type.libelle === 'Entier long')
                };
            }
        } else {
                champCleAuto = {
                    id: -1,
                    nom: 'id', 
                    typeDonnee: typesDonneePrimitifs.find(type => type.libelle === 'Entier long')
                };            
        }
        typeObjet.value.champsCles = [champCleAuto];
    } else {
        if (!estNouveauTypeObjet.value) {
            typeObjet.value.champsCles = getChampsCles(typeObjetEdite).map(chpCle => {
                return {id: chpCle.id, nom: chpCle.nom, typeDonnee: getTypeDonnee(chpCle.typeDonnee)};
            })
        }
    }
});

watch(() => typeObjet.value.champsCles.map(chpCle => chpCle.nom), () => {
    if (!estNouveauTypeObjet.value) {
        typeObjet.value.champsCles.forEach(chpCle => {
            let champExistant = typeObjetEdite.champs.find(chp => chp.nom === chpCle.nom);
            if (champExistant) {
                chpCle.id = champExistant.id;
                chpCle.typeDonnee = getTypeDonnee(champExistant.typeDonnee);
            } else {
                chpCle.id = -1;
            }
        });
    }
});

function getTypeObjetInitial() {
    return {
        id: typeObjetEdite?.id,
        nom: typeObjetEdite?.nom,
        libelle: typeObjetEdite?.libelle,
        typeObjetPere: typeObjetEdite?.idTypeObjetPere >= 0 ? getTypeObjet(typeObjetEdite?.idTypeObjetPere) : null,
        typeOccurrence: typeObjetEdite?.typeOccurrence,
        cleAuto: typeObjetEdite?.cleAuto ? true : false,
        champsCles: typeObjetEdite?.champsCles ? typeObjetEdite?.champsCles : getChampsCles(typeObjetEdite).map(chpCle => {
            return {id: chpCle.id, nom: chpCle.nom, typeDonnee: getTypeDonnee(chpCle.typeDonnee)};
        })
    };
}

function ajouterChampCle() {
    typeObjet.value.champsCles.push({
        id: -1,
        nom: null, 
        typeDonnee: null
    });
}

function supprimerChampCle(indice) {
    typeObjet.value.champsCles.splice(indice, 1);
}

function validerSaisie(typeObjet) {
    const erreurs = {};
    erreurs.nom = getErreurNomNorme(typeObjet.nom, TypeNommage.CLASSE);
    erreurs.libelle = getErreurTexteRequis(typeObjet.libelle);
    erreurs.typeOccurrence = getErreurValeurListeRequise(typeObjet.typeOccurrence);
    if (typeObjet.typeOccurrence === 'PLUSIEURS') {
        for (let [indiceChampCle, champCle] of typeObjet.champsCles.entries()) {
            erreurs[`nom_champ_cle_${indiceChampCle}`] = getErreurNomNorme(champCle.nom, TypeNommage.VARIABLE);
            erreurs[`type_champ_cle_${indiceChampCle}`] = getErreurValeurListeRequise(champCle.typeDonnee);
        }
    }    
    return erreurs;
}

function validerFormulaire() {
    typeObjet.value.validationFormulaire = true;
}

function valider() {
    console.log(typeObjet.value);
    const api = (estNouveauTypeObjet.value ? apiAjouterTypeObjet : apiModifierInfosTypeObjet);
    appelerApiModifModele(api, typeObjet.value, (reponseOK) => {
        if (reponseOK) {
            if (estNouveauTypeObjet.value) {
                const typeObjetCree = getTypeObjet(null, typeObjet.value.nom);
                emit('fermeture', typeObjetCree);
            } else {
                emit('fermeture');
            }
        }
    });
}

function gererFermeture(visible) {
    if (!visible) {
        emit('fermeture');
    }
}

</script>

<template>
    <Dialog 
    :visible="true"
    :modal="true"
    @update:visible="gererFermeture">
        <template #header>
            <label class="titre">{{ estNouveauTypeObjet ? `Ajout d'un type d'objet` : `Modification d'un type d'objet` }}</label>
        </template>
        <Formulaire
            :objet="typeObjet"
            :fonction-validation-saisie="validerSaisie"
            @validation="valider">
            <label class="intitule">Libellé :</label>
            <SaisieTexte v-model:valeur="typeObjet.libelle" :id-erreur="'libelle'" :autofocus="true" />
            <label class="intitule">Nom :</label>
            <SaisieTexte v-model:valeur="typeObjet.nom" :id-erreur="'nom'" />
            <label class="intitule">Type d'objet parent :</label>
            <SaisieTypeObjet 
                v-model:valeur="typeObjet.typeObjetPere" 
                :id-erreur="'typeObjetPere'"
                :typesObjet="typesObjetMultiples"
                :valeurOblig="false" />
            <label class="intitule">Effectif :</label>
            <SaisieTypeOccurrence 
                v-model:valeur="typeObjet.typeOccurrence" 
                :id-erreur="'typeOccurrence'"
                :desactive="typeObjet.typeObjetPere != null" />
            <template v-if="typeObjet.typeObjetPere == null && typeObjet.typeOccurrence === 'PLUSIEURS'">
                <label class="intitule">Clé automatique :</label>
                <InputSwitch 
                    v-model="typeObjet.cleAuto" />
                <label class="intitule libelle-champs-cles">Champs clés :</label>
                <div class="infos-champs-cles">
                    <label class="intitule">Nom</label>
                    <label class="intitule">Type</label>
                    <div />
                    <button 
                        class="bouton fa fa-plus btn-primaire" 
                        :disabled="typeObjet.cleAuto"
                        @click="ajouterChampCle()"/>
                    <template v-for="(champCle, indice) in typeObjet.champsCles">
                        <SaisieTexte 
                            v-model:valeur="champCle.nom" 
                            :id-erreur="`nom_champ_cle_${indice}`" />
                        <SaisieTypeDonnee 
                            v-model:valeur="champCle.typeDonnee" 
                            :id-erreur="`type_champ_cle_${indice}`"
                            :desactive="typeObjet.cleAuto || champCle.id > 0" />
                        <i class="icone-nouvel-element fas fa-circle-plus"
                            :class="{invisible: champCle.id > 0}"
                            v-tooltip="'Nouveau champ'" />
                        <button 
                            class="bouton fa fa-trash btn-supprimer" 
                            :disabled="typeObjet.champsCles.length === 1"
                            @click="supprimerChampCle(indice)"/>
                    </template>
                </div>
            </template>
        </Formulaire>
        <template #footer>
            <button 
                class="bouton btn-primaire fas fa-check" 
                @click="validerFormulaire()"/>
        </template>
    </Dialog>
</template>

<style scoped>

.titre {
    margin-right: 10px;
}

.infos-champs-cles {
    display: grid;
	grid-template-columns: auto auto auto auto;
	gap: 2px 20px;
	align-items: start;
    margin-top: 20px;
    min-width: 20vw;
}

.libelle-champs-cles {
    margin-top: 20px;
}

.icone-nouvel-element {
    color: green;
    justify-self: center;
    align-self: center;
}

</style>